import { create } from "zustand";
import type { EveContractTemplate, EveContractTemplateDetail } from "@/types/eve";

/**
 * Pipeline tab → Contract template card: the org's DocuSeal templates and
 * the selected template's prefillable fields, read through the amy action
 * route (POST /eve/v1/actions/list-contract-templates) with the Clerk
 * bearer. READ-ONLY — the save rides update-campaign-phases like every
 * other pipeline edit, so the server's validation (assignment, archived,
 * signer roles, prefill shape) stays the boundary.
 */

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

interface ContractTemplatesState {
  /** The campaign the current listing belongs to (a switch invalidates it). */
  campaignId: string | null;
  templates: EveContractTemplate[];
  selected: EveContractTemplateDetail | null;
  /** Server-side reason the requested template can't be used (unassigned / unloadable). */
  selectedError: string | null;
  shared: boolean;
  /** The server's verdict: Uplodio staff may pick/change the template; members map fields only. */
  canPickTemplate: boolean;
  isLoading: boolean;
  loaded: boolean;
  /** User-grade failure copy (DocuSeal not connected, DocuSeal down, network). */
  loadError: string | null;
  fetchTemplates: (campaignId: string, templateId: number | null, token: string) => Promise<void>;
}

interface ListResponse {
  ok: boolean;
  code?: string;
  error?: string;
  shared?: boolean;
  can_pick_template?: boolean;
  templates?: EveContractTemplate[];
  selected?: EveContractTemplateDetail | null;
  selected_error?: string;
}

export const useContractTemplatesStore = create<ContractTemplatesState>((set) => ({
  campaignId: null,
  templates: [],
  selected: null,
  selectedError: null,
  shared: false,
  canPickTemplate: false,
  isLoading: false,
  loaded: false,
  loadError: null,

  fetchTemplates: async (campaignId, templateId, token) => {
    set((s) => ({
      isLoading: true,
      loadError: null,
      // A campaign switch must never show the previous campaign's listing.
      ...(s.campaignId !== campaignId ? { campaignId, templates: [], selected: null, selectedError: null, loaded: false } : {}),
    }));
    let data: ListResponse | null = null;
    let response: Response | null = null;
    try {
      response = await fetch(`${EVE_HOST}/eve/v1/actions/list-contract-templates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          ...(templateId != null ? { template_id: templateId } : {}),
        }),
      });
      data = (await response.json()) as ListResponse;
    } catch {
      data = null;
    }
    if (!data) {
      // A non-JSON answer is a route the running amy build doesn't have (404)
      // or a crash — name the status so a stale deploy is obvious.
      set({
        isLoading: false,
        loadError: response
          ? `Amy answered ${response.status} to list-contract-templates — the amy service may need a redeploy.`
          : "Could not reach Amy — try again in a moment.",
      });
      return;
    }
    if (!data.ok) {
      set({ isLoading: false, loadError: data.error ?? "DocuSeal templates could not be loaded." });
      return;
    }
    set({
      campaignId,
      templates: data.templates ?? [],
      selected: data.selected ?? null,
      selectedError: data.selected_error ?? null,
      shared: data.shared ?? false,
      canPickTemplate: data.can_pick_template ?? false,
      isLoading: false,
      loaded: true,
      loadError: null,
    });
  },
}));
